import { observable, computed, action, extendObservable } from 'mobx';
import { get } from '@utils/get';

// 定义统一的单例 list 数据结构
// ListStore：统一管理所有业务列表数据，支持分页、错误、加载等状态
class ListStore {
    /**
     * 所有的 list 集合数据存储
     * 结构：{
     *   [namespace]: {
     *     data: { 1: [], 2: [] }, // 按页存储的列表数据
     *     requestError: { isError, errorText }, // 错误信息
     *     attribs: {}, // 附加属性（如分页信息、总数等）
     *     search: {}, // 查询条件
     *     loading: false, // 加载中
     *     hasMore: true // 是否还有更多
     *   }
     * }
     */
    /**
     * 所有的 list 集合数据存储
     * 结构：{
     *   [namespace: string]: {
     *     data: { [page: number]: any[] },
     *     requestError: { isError: boolean, errorText: string },
     *     attribs: any,
     *     search: any,
     *     loading: boolean,
     *     hasMore: boolean
     *   }
     * }
     */
    /**
     * @type {Record<string, any>}
     */
    @observable
    lists = {};

    /**
     * 注册业务list到lists集合，避免重复注册
     * @param {object} param0
     * @param {string} param0.namespace 命名空间，唯一标识一个业务列表
     */
    @action
    registerList = ({ namespace }) => {
        if (this.lists[namespace]) return;
        extendObservable(this.lists, {
            [namespace]: {
                data: {}, // 多页数据，格式：{ 1: [], 2: [] }
                requestError: {
                    isError: false,
                    errorText: '加载失败',
                },
                attribs: {}, // 附加属性（如分页、总数等）
                search: {}, // 查询条件
                loading: false, // 加载中
                hasMore: true, // 是否还有更多
            },
        });
    };

    /**
     * 设置列表数据，支持分页累加和属性同步
     * @param {object} param0
     * @param {string} param0.namespace 命名空间
     * @param {object} param0.data 接口返回的原始数据
     * @param {number} param0.page 当前页码
     * @param {string} [param0.path] 数据路径，默认'data.pageData'
     * @param {object} [param0.search] 查询条件
     * @param {boolean} [param0.hasMore] 是否还有更多
     * @param {array} [param0.extra] 需要额外同步的字段
     */
    @action
    setList = ({ namespace, data, page, path = 'data.pageData', search = {}, hasMore, extra = [] }) => {
        if (!this.lists[namespace]) {
            this.registerList({ namespace });
        }
        // 取出列表数据，支持自定义路径
        let lists = observable(get(data, path) || []);
        // 记录查询条件
        this.lists[namespace].search = search || {};
        // 按页存储数据，支持多页累加
        this.lists[namespace].data[page || 1] = lists;
        // 响应式同步分页、总数、hasMore等属性
        const attribs = {};
        if (data.data) {
            // 使用传入的 page 参数作为 currentPage，而不是从接口返回的 data.data.currentPage
            attribs.currentPage = page || data.data.currentPage;
            attribs.totalPage = data.data.totalPage;
            attribs.totalCount = data.data.totalCount;
            if (data.data.meta) {
                attribs.meta = data.data.meta;
            }
        } else {
            // 如果没有 data.data，也要设置 currentPage
            attribs.currentPage = page || 1;
        }
        attribs.page = page;
        attribs.hasMore = hasMore;
        if (extra.length) {
            extra.forEach((field) => {
                attribs[field] = get(data, 'data.' + field);
            });
        }
        // 只在首次注册时用 extendObservable，后续直接赋值
        Object.assign(this.lists[namespace].attribs, attribs);
        // 同步到根属性，保证 props 取值一致
        this.lists[namespace].hasMore = hasMore;
        // loading 也同步，直接取 lists[namespace].attribs.loading 或外部传入
        if (typeof hasMore !== 'undefined') {
            // loading 由 setAttribute 或外部控制
            // 这里不主动设置 loading，避免覆盖 setAttribute 的 loading
        }
    };

    /**
     * 设置指定列表的附加属性（如分页、总数等）
     * @param {object} param0
     * @param {string} param0.namespace 命名空间
     * @param {string} param0.key 属性名
     * @param {*} param0.value 属性值
     */
    @action
    setAttribute = ({ namespace, key, value }) => {
        if (!this.lists[namespace]) return;
        this.lists[namespace].attribs[key] = value;
        // 同步 loading 到根属性，保证 props 取值一致
        if (key === 'loading') {
            this.lists[namespace].loading = value;
        }
        this.lists = { ...this.lists };
    };

    /**
     * 获取指定列表的附加属性（如分页、总数等）
     * @param {object} param0
     * @param {string} param0.namespace 命名空间
     * @param {string} param0.key 属性名
     * @returns {*} 属性值
     */
    @action
    getAttribute = ({ namespace, key }) => {
        if (!this.lists[namespace]) return null;
        return this.lists[namespace].attribs[key];
    };

    /**
     * 获取指定命名空间的列表（拍平成一维数组，适合页面直接渲染）
     * @param {object} param0
     * @param {string} param0.namespace 命名空间
     * @returns {array} 拍平后的所有页数据
     */
    @action
    getList = ({ namespace }) => {
        if (!this.lists[namespace]) {
            this.registerList({ namespace });
            return [];
        }
        // 拍平所有页数据
        const { data } = this.lists[namespace];
        let listArray = [];
        Object.values(data).forEach((pageData) => {
            listArray = [...listArray, ...pageData];
        });
        return listArray;
    };

    /**
     * 清空指定列表及其所有状态（如翻页、错误、查询条件等）
     * @param {object} param0
     * @param {string} param0.namespace 命名空间
     */
    @action
    clearList = ({ namespace }) => {
        if (!this.lists[namespace]) return;
        this.lists[namespace].data = {};
        this.lists[namespace].attribs = {};
        this.lists[namespace].search = {};
        this.lists[namespace].requestError = {
            isError: false,
            errorText: '',
        };
        // 重置加载与翻页状态，避免沿用旧用户的 hasMore/loading
        this.lists[namespace].hasMore = true;
        this.lists[namespace].loading = false;
        this.lists = { ...this.lists };
    };

    /**
     * 错误信息（如接口请求失败時调用）
     * @param {object} param0
     * @param {string} param0.namespace 命名空間
     * @param {object} param0.value 错误对象 { isError, errorText }
     */
    @action
    setError = ({ namespace, value }) => {
        if (!this.lists[namespace]) return;
        this.lists[namespace].requestError = value;
        this.lists = { ...this.lists };
    };

    /**
     * 更新指定命名空间的列表项根据 id
     * @param {object} param0
     * @param {string} param0.namespace 命名空间
     * @param {string | number} param0.id 条目 id
     * @param {object} param0.updates 更新的属性对象
     * @returns {boolean} 是否更新成功
     */
    @action
    updateListItem = ({ namespace, id, updates }) => {
        if (!this.lists[namespace]) return false;
        
        const { data } = this.lists[namespace];
        let updated = false;
        
        // 遍历所有页的数据
        Object.keys(data).forEach((page) => {
            data[page].forEach((item, index) => {
                if (item.id === id || item._id === id) {
                    // 直接使用对象扩展语法替换整个对象，确保响应式更新
                    this.lists[namespace].data[page][index] = {
                        ...this.lists[namespace].data[page][index],
                        ...updates,
                    };
                    updated = true;
                }
            });
        });
        
        // 引发响应式更新
        if (updated) {
            this.lists = { ...this.lists };
        }
        
        return updated;
    };

    /**
     * 按自定义 key 更新指定命名空间的列表项（如 roomId）
     * @param {object} param0
     * @param {string} param0.namespace 命名空间
     * @param {string} param0.matchKey 匹配的属性名
     * @param {*} param0.matchValue 匹配的属性值
     * @param {object} param0.updates 更新的属性对象
     * @returns {boolean} 是否更新成功
     */
    @action
    updateListItemByKey = ({ namespace, matchKey, matchValue, updates }) => {
        if (!this.lists[namespace]) return false;

        const { data } = this.lists[namespace];
        let updated = false;

        // 遍历所有页的数据
        Object.keys(data).forEach((page) => {
            data[page].forEach((item, index) => {
                if (item[matchKey] === matchValue) {
                    this.lists[namespace].data[page][index] = {
                        ...this.lists[namespace].data[page][index],
                        ...updates,
                    };
                    updated = true;
                }
            });
        });

        if (updated) {
            this.lists = { ...this.lists };
        }

        return updated;
    };

    /**
     * 批量更新列表项
     * @param {object} param0
     * @param {string} param0.namespace 命名空间
     * @param {array} param0.updates 更新数组，每一项应该有 id 属性
     * @returns {boolean} 是否有更新
     */
    @action
    updateListItems = ({ namespace, updates = [] }) => {
        if (!this.lists[namespace] || updates.length === 0) return false;
        
        let hasUpdated = false;
        const { data } = this.lists[namespace];
        
        // 遍历所有页的数据
        Object.keys(data).forEach((page) => {
            data[page].forEach((item, index) => {
                // 找到匹配的更新项
                const updateItem = updates.find(
                    (u) => u.id === item.id || u._id === item._id
                );
                if (updateItem) {
                    // 直接使用对象扩展语法替换整个对象，确保响应式更新
                    this.lists[namespace].data[page][index] = {
                        ...this.lists[namespace].data[page][index],
                        ...updateItem,
                    };
                    hasUpdated = true;
                }
            });
        });
        
        // 引发响应式更新
        if (hasUpdated) {
            this.lists = { ...this.lists };
        }
        
        return hasUpdated;
    };
}

export default ListStore;